import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Plus, Trash2, XCircle } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import { registrarAbordagem, verificarIndicado } from '../../../src/services/indicacoes';
import {
  indicadosRepetidos,
  parseValorReais,
  validarIndicado,
  type IndicadoForm,
} from '../../../src/lib/indicacoes/formulario';
import { ROTULO_CANAL, ROTULO_MOTIVO, ROTULO_RESULTADO, mensagemChecagem } from '../../../src/lib/indicacoes/rotulos';
import type {
  IndicacaoCanal,
  IndicacaoChecagem,
  IndicacaoResultadoAbordagem,
  IndicacaoResultadoRegistro,
} from '../../../types';
import ModalBase from './ModalBase';
import PessoaPicker, { type PessoaEscolhida } from './PessoaPicker';
import { CLASSE_BOTAO_PRIMARIO, CLASSE_BOTAO_SECUNDARIO, CLASSE_INPUT, CLASSE_ROTULO } from './estilos';

interface LinhaIndicado extends IndicadoForm {
  chave: number;
  checagem: IndicacaoChecagem | null;
  checando: boolean;
}

const linhaVazia = (chave: number): LinhaIndicado => ({
  chave, nome: '', whatsapp: '', cpf_cnpj: '', cidade: '', valor_conta: '', checagem: null, checando: false,
});

interface Props {
  pessoaInicial?: PessoaEscolhida | null;
  gere: boolean;
  onClose: () => void;
  /** Chamado assim que o banco grava (a tela de trás recarrega). */
  onSalvo: () => void;
}

const RegistrarAbordagemModal: React.FC<Props> = ({ pessoaInicial, gere, onClose, onSalvo }) => {
  const { addToast } = useToast();
  const [pessoa, setPessoa] = useState<PessoaEscolhida | null>(pessoaInicial ?? null);
  const [canal, setCanal] = useState<IndicacaoCanal>('whatsapp');
  const [resultado, setResultado] = useState<IndicacaoResultadoAbordagem>('indicou');
  const [observacao, setObservacao] = useState('');
  const [linhas, setLinhas] = useState<LinhaIndicado[]>([linhaVazia(1)]);
  const [salvando, setSalvando] = useState(false);
  const [registro, setRegistro] = useState<IndicacaoResultadoRegistro | null>(null);

  const alterar = (chave: number, campo: keyof IndicadoForm, valor: string) =>
    setLinhas(ls =>
      ls.map(l =>
        l.chave === chave
          ? { ...l, [campo]: valor, ...(campo === 'whatsapp' || campo === 'cpf_cnpj' ? { checagem: null } : {}) }
          : l,
      ),
    );

  // Checagem ao vivo da regra A. Só roda com dado válido; o erro de formato aparece ao salvar.
  const checar = async (chave: number) => {
    const linha = linhas.find(l => l.chave === chave);
    if (!linha || !pessoa) return;
    if (!validarIndicado(linha, { whatsapp: pessoa.whatsapp, cpf_cnpj: pessoa.cpf_cnpj }).ok) return;
    setLinhas(ls => ls.map(l => (l.chave === chave ? { ...l, checando: true } : l)));
    try {
      const c = await verificarIndicado({
        whatsapp: linha.whatsapp,
        cpfCnpj: linha.cpf_cnpj,
        indicadorWhatsapp: pessoa.whatsapp,
        indicadorCpfCnpj: pessoa.cpf_cnpj,
      });
      setLinhas(ls => ls.map(l => (l.chave === chave ? { ...l, checagem: c, checando: false } : l)));
    } catch (e) {
      setLinhas(ls => ls.map(l => (l.chave === chave ? { ...l, checando: false } : l)));
      addToast((e as Error).message, 'error');
    }
  };

  const salvar = async () => {
    if (!pessoa) {
      addToast('Escolha quem foi abordado.', 'warning');
      return;
    }
    const indicados = resultado === 'indicou' ? linhas : [];
    for (const l of indicados) {
      const v = validarIndicado(l, { whatsapp: pessoa.whatsapp, cpf_cnpj: pessoa.cpf_cnpj });
      if (!v.ok) {
        addToast(`${l.nome.trim() || 'Indicado'}: ${v.erro}`, 'warning');
        return;
      }
    }
    if (indicadosRepetidos(indicados)) {
      addToast('Há indicados repetidos (mesmo WhatsApp).', 'warning');
      return;
    }
    setSalvando(true);
    try {
      const r = await registrarAbordagem({
        pessoa: pessoa.id ? { id: pessoa.id } : { nome: pessoa.nome, cpf_cnpj: pessoa.cpf_cnpj, whatsapp: pessoa.whatsapp },
        canal,
        resultado,
        observacao,
        indicados: indicados.map(l => ({
          nome: l.nome.trim(),
          whatsapp: l.whatsapp,
          cpf_cnpj: l.cpf_cnpj || null,
          cidade: l.cidade.trim() || null,
          valor_conta: parseValorReais(l.valor_conta),
        })),
      });
      setRegistro(r);
      onSalvo();
    } catch (e) {
      addToast((e as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  if (registro) {
    return (
      <ModalBase
        titulo="Abordagem registrada"
        onClose={onClose}
        largura="md"
        rodape={<button onClick={onClose} className={CLASSE_BOTAO_PRIMARIO}>Concluir</button>}
      >
        {registro.indicados.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-slate-300">A abordagem ficou salva na ficha de {pessoa?.nome}.</p>
        ) : (
          <ul className="space-y-2">
            {registro.indicados.map((r, i) => (
              <li key={i} className="flex gap-2 text-sm">
                {r.vale ? (
                  <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
                ) : (
                  <AlertTriangle size={18} className="text-amber-500 shrink-0" />
                )}
                <span className="text-gray-700 dark:text-slate-200">
                  <b>{r.nome}</b>:{' '}
                  {r.vale
                    ? `negociação #${r.negociacao_id} criada ${
                        r.responsavel_nome ? `com ${r.responsavel_nome}` : 'sem responsável (nenhuma Closer ativa)'
                      }${r.destino === 'rodizio' ? ', pelo rodízio das Closers' : ''}.`
                    : `não vale — ${r.motivo ? ROTULO_MOTIVO[r.motivo] : 'já estava na base da Vigor'}${
                        r.conflito_responsavel_nome ? ` (com ${r.conflito_responsavel_nome})` : ''
                      }. Sem recompensa.`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </ModalBase>
    );
  }

  return (
    <ModalBase
      titulo="Registrar abordagem"
      subtitulo="Pedido de indicação feito a um cliente ou a outra pessoa."
      onClose={onClose}
      largura="xl"
      rodape={
        <>
          <button onClick={onClose} className={CLASSE_BOTAO_SECUNDARIO} disabled={salvando}>
            Cancelar
          </button>
          <button onClick={salvar} className={CLASSE_BOTAO_PRIMARIO} disabled={salvando || !pessoa}>
            {salvando && <Loader2 size={15} className="animate-spin" />} Salvar
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <PessoaPicker
          valor={pessoa}
          onChange={p => {
            setPessoa(p);
            setLinhas(ls => ls.map(l => ({ ...l, checagem: null })));
          }}
          gere={gere}
          rotulo="Quem foi abordado"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={CLASSE_ROTULO}>Canal</label>
            <select className={CLASSE_INPUT} value={canal} onChange={e => setCanal(e.target.value as IndicacaoCanal)}>
              {(Object.keys(ROTULO_CANAL) as IndicacaoCanal[]).map(c => (
                <option key={c} value={c}>{ROTULO_CANAL[c]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={CLASSE_ROTULO}>Resultado</label>
            <select
              className={CLASSE_INPUT}
              value={resultado}
              onChange={e => setResultado(e.target.value as IndicacaoResultadoAbordagem)}
            >
              {(Object.keys(ROTULO_RESULTADO) as IndicacaoResultadoAbordagem[]).map(r => (
                <option key={r} value={r}>{ROTULO_RESULTADO[r]}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className={CLASSE_ROTULO}>Observação</label>
          <textarea
            className={CLASSE_INPUT}
            rows={2}
            value={observacao}
            onChange={e => setObservacao(e.target.value)}
            placeholder="Opcional"
          />
        </div>

        {resultado === 'indicou' && (
          <div className="space-y-2">
            <p className={CLASSE_ROTULO}>Pessoas indicadas</p>
            {linhas.map(l => {
              const msg = l.checagem ? mensagemChecagem(l.checagem) : null;
              return (
                <div key={l.chave} className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                    <input
                      className={`${CLASSE_INPUT} lg:col-span-2`}
                      placeholder="Nome"
                      value={l.nome}
                      onChange={e => alterar(l.chave, 'nome', e.target.value)}
                    />
                    <input
                      className={CLASSE_INPUT}
                      placeholder="WhatsApp"
                      value={l.whatsapp}
                      onChange={e => alterar(l.chave, 'whatsapp', e.target.value)}
                      onBlur={() => checar(l.chave)}
                    />
                    <input
                      className={CLASSE_INPUT}
                      placeholder="Cidade"
                      value={l.cidade}
                      onChange={e => alterar(l.chave, 'cidade', e.target.value)}
                    />
                    <input
                      className={CLASSE_INPUT}
                      placeholder="Conta de luz (R$)"
                      value={l.valor_conta}
                      onChange={e => alterar(l.chave, 'valor_conta', e.target.value)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <input
                      className={`${CLASSE_INPUT} sm:max-w-xs`}
                      placeholder="CPF/CNPJ (opcional)"
                      value={l.cpf_cnpj}
                      onChange={e => alterar(l.chave, 'cpf_cnpj', e.target.value)}
                      onBlur={() => checar(l.chave)}
                    />
                    {linhas.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setLinhas(ls => ls.filter(x => x.chave !== l.chave))}
                        className="p-1.5 rounded text-gray-400 hover:text-red-500"
                        title="Remover"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                  {l.checando && (
                    <p className="text-xs text-gray-500 inline-flex items-center gap-1">
                      <Loader2 size={12} className="animate-spin" /> Conferindo…
                    </p>
                  )}
                  {msg && (
                    <p
                      className={`text-xs inline-flex items-center gap-1 ${
                        msg.tom === 'ok' ? 'text-emerald-600' : msg.tom === 'aviso' ? 'text-amber-600' : 'text-red-600'
                      }`}
                    >
                      {msg.tom === 'ok' ? <CheckCircle2 size={13} /> : msg.tom === 'aviso' ? <AlertTriangle size={13} /> : <XCircle size={13} />}
                      {msg.texto}
                    </p>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => setLinhas(ls => [...ls, linhaVazia(Math.max(...ls.map(x => x.chave)) + 1)])}
              className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
            >
              <Plus size={13} /> Mais uma pessoa
            </button>
          </div>
        )}
      </div>
    </ModalBase>
  );
};

export default RegistrarAbordagemModal;
